import React, { useState } from 'react';
import { X, MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import './ContactUs.css';

const ContactUs = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      console.log('Sending inquiry notification...', formData);
      
      const response = await fetch('/api/notifications/inquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message
        }),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitStatus('success');
        // Clear form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        console.log('✅ Inquiry notification sent successfully');
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setSubmitStatus(null);
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
        console.error('❌ Failed to send inquiry:', result.message);
      }
      
    } catch (error) {
      console.error('❌ Error sending inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-header">
          <h2>Contact Us</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="contact-content">
          {/* Hotel Information */}
          <div className="hotel-info">
            <h3>Hotel The Container</h3>
            <div className="info-item">
              <MapPin size={20} />
              <div>
                <strong>Address:</strong>
                <p>Plot no. 20 A, Kadamwadi Rd, Kolhapur, Maharashtra 416003</p>
              </div>
            </div>
            <div className="info-item">
              <Phone size={20} />
              <div>
                <strong>Phone:</strong>
                <p>+91 7796775959</p>
              </div>
            </div>
            <div className="info-item">
              <Mail size={20} />
              <div>
                <strong>Email:</strong>
                <p>thecontainerkop@gmail.com</p>
              </div>
            </div>
            <div className="info-item">
              <Clock size={20} />
              <div>
                <strong>Check-in / Check-out:</strong>
                <p>Check-in: 12:00 PM | Check-out: 11:00 AM</p>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="map-section">
            <h4>Our Location</h4>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3823.738883861979!2d74.22394!3d16.69563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc1000cdec07a29%3A0xece8ea642952e42f!2sKolhapur%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '8px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hotel The Container Location"
              ></iframe>
            </div>
          </div>

          {/* Inquiry Form */}
          <div className="inquiry-form">
            <h4>Send us an Inquiry</h4>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="booking-inquiry">Booking Inquiry</option>
                    <option value="room-information">Room Information</option>
                    <option value="special-requests">Special Requests</option>
                    <option value="events">Events & Functions</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <Send size={16} />
                    Send Inquiry
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="submit-success">
                  ✅ Thank you! Your inquiry has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="submit-error">
                  ❌ Sorry, there was an error sending your inquiry. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
